/* ------------------------------------------------------------------
/home/cockskiz/new.greatlysocial.com/frontend/src/pages/dashboard/post/components/videoEditor/TrimTimeline.js
   Blade-style timeline ② – button to the right + clickable
------------------------------------------------------------------- */
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

/* ─────────────────────────  CSS  ───────────────────────── */
const STYLE_ID = 'trim-blade-css'
const STYLE = `
.trim-wrap    {position:absolute;left:50%;bottom:8px;transform:translateX(-50%);
               width:82%;max-width:680px;font-size:0;user-select:none;}
.trim-track   {position:relative;height:66px;border-radius:4px;background:#ddd;
               overflow:hidden;pointer-events:auto;}
.trim-thumbs  {display:flex;height:100%;width:100%;}
.trim-thumbs img{flex:1 1 0;width:100%;object-fit:cover;}
.trim-cursor  {position:absolute;top:0;width:2px;height:100%;background:#0d6efd;
               pointer-events:none;}
.trim-shade   {position:absolute;top:0;height:100%;background:rgba(0,0,0,.45);
               pointer-events:none;}
.trim-label   {position:absolute;bottom:72px;transform:translateX(-50%);
               font-size:11px;color:#fff;text-shadow:0 0 2px #000}
.trim-btn     {position:absolute;left:calc(100% + 14px);top:50%;
               transform:translateY(-50%);font-size:14px;background:#0d6efd;
               color:#fff;border:none;border-radius:4px;padding:6px 18px;
               cursor:pointer;}
`
/* inject once */
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.innerHTML = STYLE
  document.head.appendChild(el)
}

/* ─────────────────────────  component  ───────────────────────── */
export default function TrimTimeline ({
  src          = '',
  duration     = 0,
  onConfirm    = () => {},
  onHover      = () => {},
}) {
  const trackRef  = useRef()
  const cursorRef = useRef()
  const [thumbs, setThumbs] = useState([])

  const [inTime , setInTime ] = useState(null)
  const [outTime, setOutTime] = useState(null)

  /* build ~10 thumbnails (silent fail if CORS blocked) */
  useEffect(() => {
    if (!src || !duration) return
    let cancelled = false
    ;(async () => {
      try {
        const v = document.createElement('video')
        v.crossOrigin = 'anonymous'
        v.src = src
        await v.play().catch(()=>{})
        const cvs = document.createElement('canvas')
        const ctx = cvs.getContext('2d')
        const W = 160, H = 90
        cvs.width=W; cvs.height=H
        const grabs=[], step=duration/10
        for (let i=0;i<10;i++){
          /* eslint-disable no-await-in-loop */
          await new Promise(res=>{
            const seek=Math.min(duration-0.05,i*step)
            const h=()=>{ctx.drawImage(v,0,0,W,H)
              grabs.push(cvs.toDataURL('image/jpeg',.6))
              v.removeEventListener('seeked',h);res()}
            v.addEventListener('seeked',h);v.currentTime=seek})
          /* eslint-enable no-await-in-loop */
          if(cancelled)return
        }
        v.pause();if(!cancelled) setThumbs(grabs)
      } catch(e){console.warn('thumbs skipped',e)}
    })()
    return()=>{cancelled=true}
  },[src,duration])

  /* hover → move cursor + scrub preview */
  const pxToTime=x=>{
    const rect=trackRef.current.getBoundingClientRect()
    const pct=Math.min(1,Math.max(0,(x-rect.left)/rect.width))
    return pct*duration
  }
  const handleMove=e=>{
    if(!trackRef.current)return
    const x=e.touches?e.touches[0].clientX:e.clientX
    const t=pxToTime(x)
    cursorRef.current.style.left=`${(t/duration)*100}%`
    onHover(t)
  }

  /* click → set in/out */
  const handleClick=e=>{
    const x=e.touches?e.touches[0].clientX:e.clientX
    const t=+pxToTime(x).toFixed(2)
    if(inTime===null){setInTime(t)}
    else if(outTime===null && t>inTime){setOutTime(t)}
    else{setInTime(t);setOutTime(null)} // restart
  }

  /* confirm */
  const finished=inTime!==null && outTime!==null
  const handleTrim=()=>onConfirm(inTime,outTime)

  /* % helpers */
  const inPct =inTime!==null ? (inTime /duration)*100:0
  const outPct=outTime!==null? (outTime/duration)*100:100

  /* ─────────── render ─────────── */
  return (
    <div className='trim-wrap'>
      <div
        ref={trackRef}
        className='trim-track'
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onClick={handleClick}
      >
        {/* thumbs */}
        <div className='trim-thumbs'>
          {thumbs.map((t,i)=><img key={i} src={t} alt='' />)}
        </div>

        {/* live cursor */}
        <div ref={cursorRef} className='trim-cursor' />

        {/* shaded regions */}
        {inTime!==null && (
          <div className='trim-shade' style={{left:0,right:`${100-inPct}%`}}/>
        )}
        {outTime!==null && (
          <div className='trim-shade' style={{left:`${outPct}%`,right:0}}/>
        )}
      </div>

      {/* labels */}
      {inTime!==null && (
        <span className='trim-label' style={{left:`${inPct}%`}}>
          {inTime.toFixed(1)} s
        </span>
      )}
      {outTime!==null && (
        <span className='trim-label' style={{left:`${outPct}%`}}>
          {outTime.toFixed(1)} s
        </span>
      )}

      {/* button */}
      {finished && (
        <button className='trim-btn' onClick={handleTrim}>
          Trim
        </button>
      )}
    </div>
  )
}

TrimTimeline.propTypes = {
  src      : PropTypes.string,
  duration : PropTypes.number,
  onConfirm: PropTypes.func,
  onHover  : PropTypes.func,
}
